/**
 * Client-Side Cryptography Utilities
 *
 * Implements zero-knowledge encryption for the Coffre-Fort Numérique (vault).
 * All encryption/decryption operations happen client-side using the Web Crypto API.
 *
 * Security:
 * - AES-256-GCM for authenticated encryption
 * - PBKDF2-SHA256 for key derivation (600,000 iterations per OWASP 2023)
 * - Cryptographically secure random number generation
 * - Non-extractable CryptoKey objects
 */

// ============================================================================
// Types
// ============================================================================

export interface EncryptedVaultKey {
  encryptedKey: ArrayBuffer;
  iv: Uint8Array;
  authTag: Uint8Array;
}

export interface EncryptedFile {
  encryptedBlob: Blob;
  iv: Uint8Array;
  authTag: Uint8Array;
}

export interface EncryptionParams {
  algorithm: string;
  keyDerivation: string;
  iterations: number;
}

// ============================================================================
// Constants
// ============================================================================

const PBKDF2_ITERATIONS = 600000; // OWASP 2023 recommendation
const AES_KEY_LENGTH = 256; // bits
const AES_IV_LENGTH = 12; // bytes (96 bits for GCM)
const AES_TAG_LENGTH = 16; // bytes (128 bits)
const SALT_LENGTH = 16; // bytes

// ============================================================================
// Encoding Utilities
// ============================================================================

/**
 * Converts an ArrayBuffer or Uint8Array to a Base64 string
 */
export function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Converts a Base64 string to an ArrayBuffer
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Converts a hexadecimal string to an ArrayBuffer
 */
export function hexToArrayBuffer(hex: string): ArrayBuffer {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes.buffer;
}

/**
 * Converts an ArrayBuffer to a hexadecimal string
 */
export function arrayBufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

// ============================================================================
// Key Derivation
// ============================================================================

/**
 * Derives a cryptographic key from a password using PBKDF2-SHA256
 *
 * @param password - User password (NOT sent to server)
 * @param salt - Random salt (16 bytes)
 * @param iterations - Number of iterations (default: 600,000)
 * @returns CryptoKey suitable for AES-GCM encryption
 *
 * @example
 * const salt = crypto.getRandomValues(new Uint8Array(16));
 * const key = await deriveKeyFromPassword('myPassword', salt, 600000);
 */
export async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array,
  iterations: number = PBKDF2_ITERATIONS
): Promise<CryptoKey> {
  // Convert password to ArrayBuffer
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  // Import password as a CryptoKey for PBKDF2
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  // Derive AES-GCM key from password
  const derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as unknown as BufferSource,
      iterations: iterations,
      hash: 'SHA-256',
    },
    passwordKey,
    {
      name: 'AES-GCM',
      length: AES_KEY_LENGTH,
    },
    false, // not extractable (security: key stays in browser memory)
    ['encrypt', 'decrypt']
  );

  return derivedKey;
}

// ============================================================================
// Vault Master Key Generation
// ============================================================================

/**
 * Generates a random 256-bit AES-GCM vault master key
 *
 * This key is used to encrypt all documents in the vault.
 * It is itself encrypted with the user's password-derived key.
 *
 * @returns CryptoKey for AES-GCM encryption (non-extractable)
 *
 * @example
 * const vaultKey = await generateVaultMasterKey();
 */
export async function generateVaultMasterKey(): Promise<CryptoKey> {
  const vaultKey = await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: AES_KEY_LENGTH,
    },
    true, // extractable (needed to encrypt it with password-derived key)
    ['encrypt', 'decrypt']
  );

  return vaultKey;
}

// ============================================================================
// Vault Key Encryption/Decryption
// ============================================================================

/**
 * Encrypts the vault master key with a password-derived key
 *
 * The vault master key is exported as raw bytes, then encrypted with AES-GCM.
 * The encrypted key is sent to the server for storage.
 *
 * @param vaultKey - The vault master key to encrypt
 * @param derivedKey - Key derived from user's password
 * @returns Encrypted vault key with IV and auth tag
 *
 * @example
 * const vaultKey = await generateVaultMasterKey();
 * const derivedKey = await deriveKeyFromPassword('password', salt);
 * const encrypted = await encryptVaultKey(vaultKey, derivedKey);
 */
export async function encryptVaultKey(
  vaultKey: CryptoKey,
  derivedKey: CryptoKey
): Promise<EncryptedVaultKey> {
  // Export vault key as raw bytes
  const vaultKeyRaw = await crypto.subtle.exportKey('raw', vaultKey);

  // Generate random IV for AES-GCM
  const iv = crypto.getRandomValues(new Uint8Array(AES_IV_LENGTH));

  // Encrypt vault key with derived key
  const encryptedKey = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource,
      tagLength: AES_TAG_LENGTH * 8, // bits
    },
    derivedKey,
    vaultKeyRaw
  );

  // AES-GCM returns ciphertext || authTag
  const encryptedBytes = new Uint8Array(encryptedKey);
  const ciphertext = encryptedBytes.slice(0, -AES_TAG_LENGTH);
  const authTag = encryptedBytes.slice(-AES_TAG_LENGTH);

  return {
    encryptedKey: ciphertext.buffer,
    iv: iv,
    authTag: authTag,
  };
}

/**
 * Decrypts the vault master key using a password-derived key
 *
 * @param encryptedData - Encrypted vault key data (ciphertext, IV, auth tag)
 * @param derivedKey - Key derived from user's password
 * @returns Decrypted vault master key
 * @throws Error if decryption fails (wrong password or corrupted data)
 *
 * @example
 * const derivedKey = await deriveKeyFromPassword('password', salt);
 * const vaultKey = await decryptVaultKey(encryptedData, derivedKey);
 */
export async function decryptVaultKey(
  encryptedData: EncryptedVaultKey,
  derivedKey: CryptoKey
): Promise<CryptoKey> {
  // Combine ciphertext and auth tag for AES-GCM
  const encryptedBytes = new Uint8Array(
    encryptedData.encryptedKey.byteLength + encryptedData.authTag.byteLength
  );
  encryptedBytes.set(new Uint8Array(encryptedData.encryptedKey), 0);
  encryptedBytes.set(
    new Uint8Array(encryptedData.authTag),
    encryptedData.encryptedKey.byteLength
  );

  // Decrypt vault key
  let decryptedKeyRaw: ArrayBuffer;
  try {
    decryptedKeyRaw = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: encryptedData.iv as unknown as BufferSource,
        tagLength: AES_TAG_LENGTH * 8,
      },
      derivedKey,
      encryptedBytes
    );
  } catch {
    throw new Error(
      'Failed to decrypt vault key. The password may be incorrect or the data may be corrupted.'
    );
  }

  // Import decrypted key as CryptoKey
  const vaultKey = await crypto.subtle.importKey(
    'raw',
    decryptedKeyRaw,
    {
      name: 'AES-GCM',
      length: AES_KEY_LENGTH,
    },
    false, // not extractable (security)
    ['encrypt', 'decrypt']
  );

  return vaultKey;
}

// ============================================================================
// File Encryption/Decryption
// ============================================================================

/**
 * Encrypts a file using the vault master key
 *
 * The file is read as an ArrayBuffer, then encrypted with AES-256-GCM.
 * The encrypted blob is uploaded to the server along with the IV and auth tag.
 *
 * @param file - The file to encrypt
 * @param vaultKey - The vault master key
 * @returns Encrypted file blob with IV and auth tag
 *
 * @example
 * const encrypted = await encryptFile(file, vaultKey);
 * // Upload encrypted.encryptedBlob, encrypted.iv, encrypted.authTag to server
 */
export async function encryptFile(
  file: File,
  vaultKey: CryptoKey
): Promise<EncryptedFile> {
  // Read file as ArrayBuffer
  const fileBuffer = await file.arrayBuffer();

  // Generate random IV for AES-GCM
  const iv = crypto.getRandomValues(new Uint8Array(AES_IV_LENGTH));

  // Encrypt file with vault key
  const encryptedData = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource,
      tagLength: AES_TAG_LENGTH * 8,
    },
    vaultKey,
    fileBuffer
  );

  // AES-GCM returns ciphertext || authTag
  const encryptedBytes = new Uint8Array(encryptedData);
  const ciphertext = encryptedBytes.slice(0, -AES_TAG_LENGTH);
  const authTag = encryptedBytes.slice(-AES_TAG_LENGTH);

  // Create encrypted blob with same MIME type
  const encryptedBlob = new Blob([ciphertext], { type: file.type });

  return {
    encryptedBlob: encryptedBlob,
    iv: iv,
    authTag: authTag,
  };
}

/**
 * Decrypts a file using the vault master key
 *
 * @param encryptedBlob - The encrypted file blob
 * @param iv - Initialization vector used during encryption
 * @param authTag - Authentication tag for verifying integrity
 * @param vaultKey - The vault master key
 * @returns Decrypted file blob
 * @throws Error if decryption fails (corrupted data or wrong key)
 *
 * @example
 * const decryptedBlob = await decryptFile(blob, iv, authTag, vaultKey);
 * const url = URL.createObjectURL(decryptedBlob);
 * window.open(url);
 */
export async function decryptFile(
  encryptedBlob: Blob,
  iv: Uint8Array,
  authTag: Uint8Array,
  vaultKey: CryptoKey
): Promise<Blob> {
  // Read encrypted blob as ArrayBuffer
  const encryptedBuffer = await encryptedBlob.arrayBuffer();

  // Combine ciphertext and auth tag for AES-GCM
  const encryptedBytes = new Uint8Array(
    encryptedBuffer.byteLength + authTag.byteLength
  );
  encryptedBytes.set(new Uint8Array(encryptedBuffer), 0);
  encryptedBytes.set(new Uint8Array(authTag), encryptedBuffer.byteLength);

  // Decrypt file
  let decryptedData: ArrayBuffer;
  try {
    decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv as unknown as BufferSource,
        tagLength: AES_TAG_LENGTH * 8,
      },
      vaultKey,
      encryptedBytes
    );
  } catch {
    throw new Error(
      'Failed to decrypt file. The file may be corrupted or the vault key may be incorrect.'
    );
  }

  // Create decrypted blob
  const decryptedBlob = new Blob([decryptedData], {
    type: encryptedBlob.type,
  });

  return decryptedBlob;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generates a cryptographically secure random salt
 *
 * @param length - Salt length in bytes (default: 16)
 * @returns Random salt
 *
 * @example
 * const salt = generateSalt();
 */
export function generateSalt(length: number = SALT_LENGTH): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Exports a CryptoKey to JSON format for storage
 *
 * Warning: Only use this for encrypted vault keys being sent to the server.
 * Never export unencrypted keys.
 *
 * @param key - The CryptoKey to export
 * @returns JSON representation of the key
 */
export async function exportKeyToJSON(key: CryptoKey): Promise<JsonWebKey> {
  return await crypto.subtle.exportKey('jwk', key);
}

/**
 * Imports a CryptoKey from JSON format
 *
 * @param jwk - JSON Web Key
 * @param algorithm - Algorithm name (default: 'AES-GCM')
 * @param extractable - Whether the key is extractable (default: false)
 * @param usages - Key usages (default: ['encrypt', 'decrypt'])
 * @returns Imported CryptoKey
 */
export async function importKeyFromJSON(
  jwk: JsonWebKey,
  algorithm: string = 'AES-GCM',
  extractable: boolean = false,
  usages: KeyUsage[] = ['encrypt', 'decrypt']
): Promise<CryptoKey> {
  return await crypto.subtle.importKey(
    'jwk',
    jwk,
    {
      name: algorithm,
      length: AES_KEY_LENGTH,
    },
    extractable,
    usages
  );
}

// ============================================================================
// RSA Handlers (Zero-Knowledge Automation)
// ============================================================================

const RSA_ALGORITHM = 'RSA-OAEP';
const RSA_MODULUS_LENGTH = 4096;
const RSA_HASH = 'SHA-256';

/**
 * Generates an RSA-OAEP key pair for the vault
 */
export async function generateRSAKeyPair(): Promise<CryptoKeyPair> {
  return await crypto.subtle.generateKey(
    {
      name: RSA_ALGORITHM,
      modulusLength: RSA_MODULUS_LENGTH,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: RSA_HASH,
    },
    true, // extractable (need to export public key and encrypt private key)
    ['encrypt', 'decrypt']
  );
}

/**
 * Exports a Public Key to PEM format
 */
export async function exportPublicKeyToPEM(
  publicKey: CryptoKey
): Promise<string> {
  const exported = await crypto.subtle.exportKey('spki', publicKey);
  const exportedAsBase64 = arrayBufferToBase64(exported);
  return `-----BEGIN PUBLIC KEY-----\n${exportedAsBase64}\n-----END PUBLIC KEY-----`;
}

/**
 * Exports a Private Key to PEM format
 * (Careful: This exposes the private key in cleartext memory)
 */
export async function exportPrivateKeyToPEM(
  privateKey: CryptoKey
): Promise<string> {
  const exported = await crypto.subtle.exportKey('pkcs8', privateKey);
  const exportedAsBase64 = arrayBufferToBase64(exported);
  return `-----BEGIN PRIVATE KEY-----\n${exportedAsBase64}\n-----END PRIVATE KEY-----`;
}

/**
 * Encrypts the RSA Private Key with the Vault Master Key (AES-GCM)
 */
export async function encryptRSAPrivateKey(
  privateKey: CryptoKey,
  vaultMasterKey: CryptoKey
): Promise<EncryptedVaultKey> {
  // Export private key as PKCS8 (binary)
  const privateKeyBytes = await crypto.subtle.exportKey('pkcs8', privateKey);

  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(AES_IV_LENGTH));

  // Encrypt with Vault Master Key
  const encryptedParams = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource,
      tagLength: AES_TAG_LENGTH * 8,
    },
    vaultMasterKey,
    privateKeyBytes
  );

  // Split ciphertext and tag
  const encryptedBytes = new Uint8Array(encryptedParams);
  const ciphertext = encryptedBytes.slice(0, -AES_TAG_LENGTH);
  const authTag = encryptedBytes.slice(-AES_TAG_LENGTH);

  return {
    encryptedKey: ciphertext.buffer,
    iv: iv,
    authTag: authTag,
  };
}

/**
 * Decrypts the RSA Private Key using the Vault Master Key
 */
export async function decryptRSAPrivateKey(
  encryptedData: EncryptedVaultKey,
  vaultMasterKey: CryptoKey
): Promise<CryptoKey> {
  // Combine ciphertext and auth tag
  const encryptedBytes = new Uint8Array(
    encryptedData.encryptedKey.byteLength + encryptedData.authTag.byteLength
  );
  encryptedBytes.set(new Uint8Array(encryptedData.encryptedKey), 0);
  encryptedBytes.set(
    new Uint8Array(encryptedData.authTag),
    encryptedData.encryptedKey.byteLength
  );

  // Decrypt
  let decryptedBytes: ArrayBuffer;
  try {
    decryptedBytes = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: encryptedData.iv as unknown as BufferSource,
        tagLength: AES_TAG_LENGTH * 8,
      },
      vaultMasterKey,
      encryptedBytes
    );
  } catch {
    throw new Error('Failed to decrypt RSA Private Key.');
  }

  // Import as CryptoKey
  return await crypto.subtle.importKey(
    'pkcs8',
    decryptedBytes,
    {
      name: RSA_ALGORITHM,
      hash: RSA_HASH,
    },
    false, // not extractable after unlock (security)
    ['decrypt']
  );
}

/**
 * Decrypts a file key (AES) using the RSA Private Key
 * (Used when opening an automatically deposited document)
 */
export async function decryptFileKeyWithRSA(
  encryptedFileKey: ArrayBuffer,
  privateKey: CryptoKey
): Promise<ArrayBuffer> {
  return await crypto.subtle.decrypt(
    {
      name: RSA_ALGORITHM,
    },
    privateKey,
    encryptedFileKey
  );
}
